import {
  Grid,
  Flexbox,
  Card,
  FormField,
  Stepper,
  Button,
  Modal,
  BaseTable,
  TextInput,
  Select,
  useFileXhrUploader
} from '@sede-enterprise/shell-ds-react-framework';
import {
  OnError,
  OnProgress,
} from '@sede-enterprise/shell-ds-react-framework/build/esm/components/FileUploader/FileUploader.types.js';
import React, { useState, useCallback, useMemo, ReactNode, ChangeEvent } from 'react';
import ReusableFileUploader from '../../../Tanso/components/pages/SectoralPerformance/ReusableFileUploader';
import { useFileUploadEffect } from '../../../Tanso/components/pages/SectoralPerformance/useFileUploadEffect';
import { removeDeleteLinkInFileUploader } from '../../../Tanso/components/pages/SectoralPerformance/SectoralInfoUtils';
import {
  processExcelFile,
  exportToExcel,
  filterDataBySearchTerm,
  generateAvailableYears,
  getBusinessSegmentOptions,
  getTableColumns,
} from "./QuarterlyRefreshUtils/CommonUtils2";
import { THOUSAND_FIVE_HUNDRED } from '../../api/constants';

// Define types
interface QuarterlyRefreshFileUploadProps {
  updateInfo?: ReactNode;
}

interface ProcessedResults {
  finalResult: Record<string, unknown>;
  exportData: {
    combinedData: Record<string, string | number>[];
  };
}

interface BusinessSegmentOption {
  label: string;
  value: string;
}

// Constants for file validation
const EXCEL_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel'
];
const EXCEL_EXTENSIONS = ['.xlsx', '.xls'];

// Constants for UI state
const UPLOAD_STATE = {
  NONE: 'none',
  SUCCESS: 'success',
  ERROR: 'error',
  INVALID: 'invalid'
} as const;

type UploadStateType = typeof UPLOAD_STATE[keyof typeof UPLOAD_STATE];

// Constants for modal messages
const MODAL_MESSAGES = {
  FILE_UPLOAD_SUCCESS: 'Excel file processed successfully.',
  EXPORT_SUCCESS: 'Data exported successfully.'
};

// Constants for error messages
const ERROR_MESSAGES = {
  INVALID_FILE: 'Please upload a valid Excel file (.xlsx or .xls)',
  NO_DATA: 'No data available to export',
  NO_FILE: 'Please upload a file before submitting'
};

const QuarterlyRefreshFileUpload: React.FC<QuarterlyRefreshFileUploadProps> = ({ 
  updateInfo = null 
}: QuarterlyRefreshFileUploadProps = {}) => {
  // State for modals
  const [isFileUploadSuccess, setIsFileUploadSuccess] = useState<boolean>(false);
  const [isExportSuccess, setIsExportSuccess] = useState<boolean>(false);

  // State for data
  const [processedResults, setProcessedResults] = useState<ProcessedResults | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  // State for file handling
  const [uploadState, setUploadState] = useState<UploadStateType>(UPLOAD_STATE.NONE);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // Add state for file upload success (different from process success)
  const [isUploadSuccess, setIsUploadSuccess] = useState<boolean>(false);

  // State for options
  const [businessSegment, setBusinessSegment] = useState<string>('SE');
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<string>(currentYear.toString());

  // Initialize useFileXhrUploader for the handleOnDrop method
  const url = '';
  const { handleOnDrop: xhrHandleOnDrop } = useFileXhrUploader({
    url,
  });

  // Apply the useFileUploadEffect hook to hide progress and text
  useFileUploadEffect('quarterlyRefreshFileId', isUploadSuccess, uploadState);

  // Memoized values
  const availableYears = useMemo(() =>
    generateAvailableYears(currentYear), [currentYear]);

  const availableBusinessSegments = useMemo<BusinessSegmentOption[]>(() =>
    getBusinessSegmentOptions(), []);

  // Table columns
  const columns = useMemo(() => getTableColumns(), []);

  // Validate if file is Excel
  const isExcelFile = useCallback((file: File): boolean => {
    return EXCEL_MIME_TYPES.includes(file.type) ||
      EXCEL_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext));
  }, []);

  // Handle file drop
  const handleOnDrop = useCallback((
    files: File[], 
    onProgress: (fileName: string, progress?: number) => void, 
    onError: (message: string) => void
  ): void => {
    if (files && files.length > 0) {
      // Use the built-in drop handler from useFileXhrUploader
      xhrHandleOnDrop(files, (fileName: string, progress?: number) => onProgress(fileName, progress ?? 0), onError);
    } else {
      onError('No file selected');
    }
  }, [xhrHandleOnDrop]);

  // Handle file upload - now just stores the file, without processing
  const handleOnUpload = useCallback((files: File[]): void => {
    if (files && files.length > 0) {
      const file = files[0];

      if (isExcelFile(file)) {
        // Just store the file, don't process it yet
        setUploadedFile(file);
        setUploadState(UPLOAD_STATE.SUCCESS);
        setErrorMessage('');
        
        // Set upload success to true to trigger useFileUploadEffect
        setIsUploadSuccess(true);
        
        // Reset any previous results
        setProcessedResults(null);
      } else {
        setUploadState(UPLOAD_STATE.INVALID);
        setErrorMessage(ERROR_MESSAGES.INVALID_FILE);
        setUploadedFile(null);
        setIsUploadSuccess(false);
      }
    }
  }, [isExcelFile]);

  // New submit handler to process the file
  const handleSubmit = useCallback((): void => {
    if (!uploadedFile) {
      setUploadState(UPLOAD_STATE.ERROR);
      setErrorMessage(ERROR_MESSAGES.NO_FILE);
      return;
    }

    // Process the file
    processExcelFile(
      uploadedFile,
      {
        setUploadState: setUploadState as (state: string) => void,
        setErrorMessage,
        setExcelData: () => {}, // Pass a no-op function since we don't use excelData
        setProcessedResults: setProcessedResults as (results: ProcessedResults) => void,
        setIsFileUploadSuccess, // Add this line
        businessSegment,
        selectedYear
      }
    );
  }, [uploadedFile, businessSegment, selectedYear]);

  // Reset/Cancel handler
  const handleCancel = useCallback((): void => {
    // Reset all state to starting values
    setUploadedFile(null);
    setProcessedResults(null);
    setUploadState(UPLOAD_STATE.NONE);
    setErrorMessage('');
    setSearchTerm('');
    setBusinessSegment('SE');
    setSelectedYear(currentYear.toString());
    setIsUploadSuccess(false);
    
    // Remove the delete link in the file uploader
    removeDeleteLinkInFileUploader('quarterlyRefreshFileId');
  }, [currentYear]);

  // Handle file removal
  const handleRemoveFile = useCallback((): void => {
    setUploadedFile(null);
    setProcessedResults(null);
    setUploadState(UPLOAD_STATE.NONE);
    setErrorMessage('');
    setSearchTerm('');
    setIsUploadSuccess(false);
    
    // Remove the delete link in the file uploader
    removeDeleteLinkInFileUploader('quarterlyRefreshFileId');
  }, []);

  // Handle export data to Excel
  const handleExportData = useCallback((): void => {
    if (!processedResults?.exportData) {
      setUploadState(UPLOAD_STATE.ERROR);
      setErrorMessage(ERROR_MESSAGES.NO_DATA);
      return;
    }

    try {
      // Construct filename with segment and year
      const fileName = `carbon_emissions_${businessSegment}_${selectedYear}.xlsx`;

      // Export the combined data to Excel
      exportToExcel(
        processedResults.exportData.combinedData,
        fileName
      );

      // Show success feedback
      setIsExportSuccess(true);
      setTimeout(() => setIsExportSuccess(false), THOUSAND_FIVE_HUNDRED);
    } catch (error) {
      setUploadState(UPLOAD_STATE.ERROR);
      setErrorMessage(`Error exporting data: ${error instanceof Error ? error.message : String(error)}`);
    }
  }, [processedResults, businessSegment, selectedYear]);

  // Filter data based on search term
  const filteredData = useMemo(() =>
    filterDataBySearchTerm(
      processedResults?.exportData?.combinedData ?? [],
      searchTerm
    ),
    [processedResults, searchTerm]
  );

  // Modal close handlers
  const closeFileUploadModal = useCallback((): void => {
    setIsFileUploadSuccess(false);
  }, []);

  const closeExportModal = useCallback((): void => {
    setIsExportSuccess(false);
  }, []);

  // Handle search clear
  const handleClearSearch = useCallback((): void => {
    setSearchTerm('');
  }, []);

  // Handle search input change
  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.target.value);
  }, []);

  // Convert year to option format
  const yearOptions = useMemo(() =>
    availableYears.map(year => ({ label: year, value: year })),
    [availableYears]
  );

  return (
    <Card
      className="card"
      data-testid="quarterly-refresh-file-upload"
      bodyPadding
      header={{
        title: 'Upload Data File',
        description: 'Upload the consolidated Excel file for the quarterly data refresh. All sheets will be processed automatically.',
      }}
      style={{
        borderRadius: '4px',
        background: 'var(--Background-Surface, #FFF)',
        boxShadow: '0px 6px 24px 0px rgba(0, 0, 0, 0.06), 0px 2px 6px 0px rgba(0, 0, 0, 0.10)',
        marginTop: '16px',
      }}
    >
      <div
        style={{
          borderTop: '1px solid var(--Border-Subtle, rgba(28, 28, 28, 0.04))',
          paddingTop: '16px',
        }}
      >
        {isFileUploadSuccess && (
          <Modal
            open={true}
            onClose={closeFileUploadModal}
          >
            <div>{MODAL_MESSAGES.FILE_UPLOAD_SUCCESS}</div>
          </Modal>
        )}

        {isExportSuccess && (
          <Modal
            open={true}
            onClose={closeExportModal}
          >
            <div>{MODAL_MESSAGES.EXPORT_SUCCESS}</div>
          </Modal>
        )}

        {/* Filter options section */}
        <div style={{ marginBottom: '24px' }}>
          <h4>Configuration Options</h4>
          <Grid flow="row" gap="16px">
            <Grid.Cell width={6}>
              <FormField label="Business Segment">
                <Select
                  options={availableBusinessSegments}
                  value={businessSegment}
                  onChange={setBusinessSegment}
                  data-testid="business-segment-select"
                />
              </FormField>
            </Grid.Cell>
            <Grid.Cell  width={6}>
              <FormField label="Year">
                <Select
                  options={yearOptions}
                  value={selectedYear}
                  onChange={setSelectedYear}
                  data-testid="year-select"
                />
              </FormField>
            </Grid.Cell>
          </Grid>
        </div>

        <Flexbox justifyContent="center">
          <FormField icon={<Stepper.Step order={1} last title="Quarterly Refresh File" />}>
            <ReusableFileUploader
              fileId="quarterlyRefreshFileId"
                 onDrop={(files: File[], onProgress: OnProgress, onError: OnError) =>
                              handleOnDrop(files, (fileName, progressPercentage) => onProgress(fileName, progressPercentage ?? 0), onError)
                            }
              onUpload={handleOnUpload}
              onRemoveFile={handleRemoveFile}
              uploadState={uploadState}
              uploadFailureMessage={errorMessage}
            />
          </FormField>
        </Flexbox>

        {/* Submit and Cancel buttons - only enabled when a file is uploaded */}
        <Flexbox justifyContent="center" gap="16px" style={{ marginTop: '16px' }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={handleSubmit}
            disabled={!uploadedFile}
            data-testid="submit-button"
          >
            Submit
          </Button>
        </Flexbox>

        {processedResults && (
          <div style={{ marginTop: '24px', marginBottom: '24px' }}>
            <h4>Data processed successfully</h4>
            <Flexbox justifyContent="space-between" alignItems="center">
              <p>Carbon intensity data is ready for export.</p>
              <Button
                variant="filled"
                onClick={handleExportData}
                disabled={!processedResults}
                data-testid="export-data-button"
                style={{ fontSize: 'inherit', fontWeight: 'inherit' }}
              >
                Export Data
              </Button>
            </Flexbox>

            {/* Data table with search - always shown when data is processed */}
            <div style={{ marginTop: '16px' }}>
              <h4>Export Data Preview</h4>

              {/* Search input */}
              <div style={{ marginBottom: '16px', marginTop: '12px' }}>
                <TextInput
                  placeholder="Search all columns..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  style={{ width: '300px' }}
                />
                {searchTerm && (
                  <div style={{ marginTop: '8px' }}>
                    <Button
                      variant="outlined"
                      onClick={handleClearSearch}
                      size="small"
                    >
                      Clear Search
                    </Button>
                    <span style={{ marginLeft: '8px' }}>
                      Showing {filteredData.length} of {processedResults.exportData.combinedData.length} entries
                    </span>
                  </div>
                )}
              </div>

              <div style={{ overflowX: 'auto' }}>
                <BaseTable
                  columns={columns}
                  data={filteredData}
                  size="small"
                  data-testid="export-data-table"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          </div>
        )}

        <Grid flow="column" justifyContent="space-between" style={{ marginTop: '24px' }}>
          <Grid.Cell>
            {updateInfo}
          </Grid.Cell>
        </Grid>
      </div>
    </Card>
  );
};

export default QuarterlyRefreshFileUpload;